import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import tourGlossaryMessages from 'hooks/Tours/tourGlossaryMessages'

function useTourStyledLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const styledProductTypeTitles: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: 'C23n9vc69HQVczPw0q7C',
        defaultMessage: 'Deluxe',
        description: 'Tour product type title for deluxe tours on the tour page',
      }),
      premium_tour: intl.formatMessage({
        id: 'VWQ3t7-7GdR20QlFbd_E',
        defaultMessage: 'Premium',
        description: 'Tour product type title for premium tours on the tour page',
      }),
      ultra_lux_tour: intl.formatMessage(productLabels.ultraLux),
      signature_series_tour: intl.formatMessage({
        id: 'SGzBydaebDZ8CnxQ6gFM',
        defaultMessage: 'Signature Series',
        description: 'Tour product type title for signature series tours',
      }),
      custom_tour: intl.formatMessage({
        id: 'CR2Mqw1P4INsuaA5rRfZ',
        defaultMessage: 'Custom',
        description: 'Tour product type title for custom tours on the tour page',
      }),
      insider_series_tour: intl.formatMessage({
        id: 'ibfwnTAGxkDp3ybhfvtD',
        defaultMessage: 'Insider Series',
        description: 'Tour product type title for insider series tours on offer page',
      }),
    }

    const styledHeading: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.deluxeTours),
      }),
      premium_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.premiumTours),
      }),
      ultra_lux_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.ultraLuxTours),
      }),
      signature_series_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.signatureSeriesTours),
      }),
      custom_tour: intl.formatMessage({
        id: 'pnBKTW4A-iMcbEj5Q3Ds',
        defaultMessage: 'Key Benefits of your once-in-a-lifetime tour',
        description: 'Section heading for key benefits of custom once-in-a-lifetime tours',
      }),
      insider_series_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.insiderSeriesTours),
      }),
    }

    const privateStyledHeading: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.privateDeluxeTours),
      }),
      premium_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.privatePremiumTours),
      }),
      ultra_lux_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.privateUltraLuxTours),
      }),
      signature_series_tour: intl.formatMessage(tourGlossaryMessages.keyBenefitsOf, {
        tourVariantName: intl.formatMessage(tourGlossaryMessages.privateSignatureSeriesTours),
      }),
      custom_tour: intl.formatMessage({
        id: 'L8FPYD8w2w2TxU1k6Mcf',
        defaultMessage: 'Key benefits of your private once-in-a-lifetime tour',
        description: 'Section heading for key benefits of private custom tours',
      }),
      insider_series_tour: intl.formatMessage({
        id: 'G7tVoRYxEY7rCcY23zkd',
        defaultMessage: 'Key benefits of your private Insider Series tour',
        description: 'Section heading for key benefits of private insider series tours',
      }),
    }

    const styledProductTypeHotelBenefits: Record<App.Tours.V2ProductTypeStyled, string> = {
      deluxe_tour: intl.formatMessage({
        id: 'xU91-7Y4YPBh9taho0iM',
        defaultMessage: '4 & 4.5 star hotels',
        description: 'Tour hotel benefit description for deluxe tours on the tour page',
      }),
      premium_tour: intl.formatMessage({
        id: 'dZ2Aoa5FJNYBfQ5hQ6Pf',
        defaultMessage: '5 star hotels',
        description: 'Tour hotel benefit description for premium tours on the tour page',
      }),
      ultra_lux_tour: intl.formatMessage({
        id: 'IkEBSXPmk7HmpFexzQDu',
        defaultMessage: '5 star hotels & iconic stays',
        description: 'Tour hotel benefit description for ultra lux tours',
      }),
      signature_series_tour: intl.formatMessage({
        id: 'BFPuGX81IGOlroqi3_-l',
        defaultMessage: '5 star hotels',
        description: 'Tour hotel benefit description for signature series tours',
      }),
      custom_tour: intl.formatMessage({
        id: 'cyHDoazpcMtTwNTnY9nz',
        defaultMessage: '4 & 5 star hotels',
        description: 'Tour hotel benefit description for custom tours on the tour page',
      }),
      insider_series_tour: '',
    }

    const packageUpgradeDisplayText = {
      REGULAR: intl.formatMessage({
        id: 'zU159GKzORAYE0CDxuiQ',
        defaultMessage: 'Standard Package',
        description: 'Tour package upgrade display text for the standard/regular package tier',
      }),
      VIP: intl.formatMessage({
        id: 'S8-FScl9Ln2H5LZCAGW9',
        defaultMessage: 'Upgraded Package',
        description: 'Tour package upgrade display text for the VIP/upgraded package tier',
      }),
      PLATINUM: intl.formatMessage({
        id: 'Y_qocUusQWI3GQ2xmEIV',
        defaultMessage: 'Deluxe Package',
        description: 'Tour package upgrade display text for the platinum/deluxe package tier',
      }),
    }

    const tourArtefactTypes: Record<App.Tours.ArtefactType, string> = {
      'invitation-letter': intl.formatMessage({
        id: '2H9OjBgrVd_ugNM0-Mi7',
        defaultMessage: 'Letter of Invitation Form',
        description: 'Tour artefact type label for letter of invitation form',
      }),
      'visa': intl.formatMessage({
        id: 'gP9hyw14Cys8Vp8a8mO9',
        defaultMessage: 'Copy of Visa into Destination Country',
        description: 'Tour artefact type label for visa copy into destination country',
      }),
      'passport-photo': intl.formatMessage({
        id: 'ir6WDn-pjVoAKO1Z-pKZ',
        defaultMessage: 'Passport',
        description: 'Tour artefact type label for passport document in the tours section',
      }),
      'passport-photograph': intl.formatMessage({
        id: 'VL9vSVAiukQ089GB37Rm',
        defaultMessage: 'Passport-Sized Photo',
        description: 'Tour artefact type label for passport-sized photograph',
      }),
    }

    return {
      styledProductTypeTitles,
      styledHeading,
      privateStyledHeading,
      styledProductTypeHotelBenefits,
      packageUpgradeDisplayText,
      tourArtefactTypes,
    }
  }, [intl])
}

export default useTourStyledLabels
